/**
 * WorkflowSplitPage · Side-by-side Studio + Runner
 * ─────────────────────────────────────────────────
 * Two iframes with a draggable divider. Edit on one side, see the effect
 * on the other. "Refresh Runner" after saving a Studio change to see it live.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowLeftRight, X, Home, ExternalLink } from 'lucide-react';

const STUDIO_URL = '/settings/status-workflows';
const RUNNER_URL = '/workflow/start';

export default function WorkflowSplitPage() {
  const navigate = useNavigate();
  const [leftPct, setLeftPct] = useState(50);
  const [swapped, setSwapped] = useState(false);
  const [leftKey, setLeftKey] = useState(0);
  const [rightKey, setRightKey] = useState(0);
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  // Drag divider
  const onMouseDown = () => { draggingRef.current = true; document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; };
  const onMouseUp = useCallback(() => {
    if (draggingRef.current) {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, []);
  const onMouseMove = useCallback((e) => {
    if (!draggingRef.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setLeftPct(Math.max(20, Math.min(80, pct)));
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const studioSrc = STUDIO_URL + '?embed=1';
  const runnerSrc = RUNNER_URL + '?embed=1';

  const leftIsStudio = !swapped;
  const leftSrc = leftIsStudio ? studioSrc : runnerSrc;
  const rightSrc = leftIsStudio ? runnerSrc : studioSrc;
  const leftLabel = leftIsStudio ? 'Studio · edit templates' : 'Runner · walk through';
  const rightLabel = leftIsStudio ? 'Runner · walk through' : 'Studio · edit templates';

  const refreshLeft = () => setLeftKey(k => k + 1);
  const refreshRight = () => setRightKey(k => k + 1);
  const refreshBoth = () => { refreshLeft(); refreshRight(); };

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-3 flex-shrink-0">
        <button onClick={() => navigate('/workflow/start')}
          data-testid="split-home-btn"
          className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
          <Home size={14} /> Runner Home
        </button>
        <span className="text-slate-300">·</span>
        <h1 className="text-sm font-bold text-slate-800">Split View · Studio + Runner</h1>
        <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Edit ⇄ Test in real-time
        </span>
        <span className="flex-1" />

        <button onClick={refreshBoth} data-testid="split-refresh-both"
          className="text-xs px-3 py-1.5 rounded bg-sky-600 text-white font-semibold hover:bg-sky-700 flex items-center gap-1">
          <RefreshCw size={12} /> Refresh both
        </button>
        <button onClick={() => setSwapped(v => !v)} data-testid="split-swap"
          className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 flex items-center gap-1">
          <ArrowLeftRight size={12} /> Swap sides
        </button>
        <button onClick={() => navigate(-1)} data-testid="split-close"
          className="text-slate-400 hover:text-rose-500 p-1"><X size={16} /></button>
      </div>

      {/* Split panes */}
      <div ref={containerRef} className="flex-1 flex overflow-hidden">
        {/* Left pane */}
        <div className="flex flex-col bg-white border-r border-slate-200 overflow-hidden" style={{ width: `${leftPct}%` }}>
          <PaneHeader label={leftLabel} url={leftSrc.replace('?embed=1', '')} onRefresh={refreshLeft} onOpen={leftSrc} />
          <iframe key={leftKey} src={leftSrc} title="left-pane"
            data-testid="split-left-iframe"
            className="flex-1 w-full border-0" />
        </div>

        {/* Divider */}
        <div onMouseDown={onMouseDown}
          data-testid="split-divider"
          className="w-1 bg-slate-200 hover:bg-sky-400 cursor-col-resize relative group flex-shrink-0">
          <div className="absolute inset-y-0 -left-1 -right-1" />
          <div className="absolute top-1/2 -translate-y-1/2 -left-2 w-5 h-12 bg-slate-300 group-hover:bg-sky-500 rounded flex items-center justify-center transition">
            <div className="flex flex-col gap-0.5">
              <div className="w-0.5 h-1 bg-white rounded-full" />
              <div className="w-0.5 h-1 bg-white rounded-full" />
              <div className="w-0.5 h-1 bg-white rounded-full" />
            </div>
          </div>
        </div>

        {/* Right pane */}
        <div className="flex flex-col bg-white overflow-hidden" style={{ width: `${100 - leftPct}%` }}>
          <PaneHeader label={rightLabel} url={rightSrc.replace('?embed=1', '')} onRefresh={refreshRight} onOpen={rightSrc} />
          <iframe key={rightKey} src={rightSrc} title="right-pane"
            data-testid="split-right-iframe"
            className="flex-1 w-full border-0" />
        </div>
      </div>
    </div>
  );
}

function PaneHeader({ label, url, onRefresh, onOpen }) {
  return (
    <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2 flex-shrink-0">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <code className="text-[10px] text-slate-400 font-mono">{url}</code>
      <span className="flex-1" />
      <button onClick={onRefresh} title="Refresh this pane"
        className="text-slate-400 hover:text-sky-600 p-1"><RefreshCw size={12} /></button>
      <a href={onOpen.replace('?embed=1', '')} target="_blank" rel="noopener noreferrer"
        title="Open full-screen in new tab"
        className="text-slate-400 hover:text-sky-600 p-1"><ExternalLink size={12} /></a>
    </div>
  );
}
